//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import { useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import SimpleBar from 'simplebar-react';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { logPageView } from '../system/firebase';
import { LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import { PAGE_TITLE_EMP_SEARCH } from '../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
// import { loadAdvertisementsByPage } from '../common/services/adServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import TopNav from '../topNav/TopNav';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import EmpFilterPanel from './subComponents/FilterPanel';
import EmpListing from './subComponents/Listing';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';
// import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    pageContainer: { overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
    pageHeader: { maxHeight: '81px', [theme.breakpoints.down('sm')]: { maxHeight: '81px' } },
    pageContent: {
        flex: 1,
        overflowY: 'hidden',
        overflowX: 'hidden',
        [theme.breakpoints.down('md')]: {
            overflowY: 'scroll',
            overflowX: 'hidden',
            '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                background: 'none',
            },
            '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F5F5F5',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0,0,0,0.2)',
            },
        },
    },
    titleContainer: {
        padding: '10px 60px 5px 60px',
        [theme.breakpoints.down('sm')]: { padding: '10px 15px 5px 15px' },
    },
    bodyContainer: {
        // height: '100%',
        padding: '0px 60px',
        [theme.breakpoints.down('sm')]: { padding: '0px 15px' },
    },
    filterGrid: {
        [theme.breakpoints.up('lg')]: { overflowY: 'hidden' },
    },
    listGrid: {
        //height: '100vh',
        overflow: 'hidden',
    },
    heading: {
        fontSize: '26px',
        fontWeight: 'bold',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        query,
        setQuery,
        showAds,
        setShowAds,
        advertisements,
    } = params;

    const defns = {
        pageContainer: { container: true, direction: 'column' },
        pageHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        pageContent: { item: true, container: true, lg: 12, md: 12, sm: 12, xs: 12 },
        filterGrid: { item: true, lg: 4, md: 12, sm: 12, xs: 12 },
        listGrid: { item: true, lg: 8, md: 12, sm: 12, xs: 12 },
        listing: { query, setQuery, showAds, setShowAds, advertisements },
        titleContainer: { item: true, container: true, justifyContent: 'space-between' },
        bodyContainer: { item: true, container: true, justifyContent: 'space-between' },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const queryInitState = {
    searchText: null,
    industry: null,
    companySize: null,
    location: null,
};
// const timeInterval = minutesToMilliseconds(30);

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const EmpSearchLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();

    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    const context = useContext(LayoutContext);
    // const { setSnackbarMessage, setSnackbarErrorMessage } = context;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { isShowJobs } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [haveActiveJobs, setHaveActiveJobs] = useState(false);
    // const [sortTag, setSortTag] = useState(ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT);
    const [query, setQuery] = useState(queryInitState);
    const [searchLocation, setSearchLocation] = useState(null);
    const [showAds, setShowAds] = React.useState(true);
    const [advertisements] = React.useState([]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_EMP_SEARCH);
    }, []);

    // // load Advertisements
    // React.useEffect(() => {
    //     loadAdvertisementsByPage({ empSearch: true, callBack: onServiceCallBack });
    //     setInterval(() => {
    //         loadAdvertisementsByPage({ empSearch: true, callBack: onServiceCallBack });
    //     }, timeInterval);
    // }, []);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    // const onServiceCallBack = data => {
    //     setAdvertisements(data);
    // };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, query, setQuery, showAds, setShowAds, advertisements });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context, queryInitState, query, setQuery, searchLocation, setSearchLocation, haveActiveJobs, setHaveActiveJobs }}>
            <AfitdPageHead title={PAGE_TITLE_EMP_SEARCH} />
            <Grid {...defns.pageContainer}>
                <Grid {...defns.pageHeader}>
                    <TopNav />
                </Grid>
                <Grid {...defns.pageContent}>
                    <SimpleBar style={{ width: '100%', height: '100%' }}>
                        <Grid {...defns.titleContainer}>
                            <h2 {...defns.heading}>Employer Search</h2>
                        </Grid>
                        <Grid {...defns.bodyContainer}>
                            <Grid {...defns.filterGrid}>
                                <EmpFilterPanel setShowAds={setShowAds} />
                            </Grid>
                            <Grid {...defns.listGrid}>
                                <EmpListing {...defns.listing} />
                            </Grid>
                        </Grid>
                    </SimpleBar>
                </Grid>
            </Grid>
        </LayoutContext.Provider>
    );
};

export default EmpSearchLayout;
